import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || '',
})

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  // TODO: Get token from auth context
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const apiBase = () => (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '')

// A GET that tries again when the failure is the kind that passes. While the
// API gateway is over its rate limit it refuses a share of requests: as a
// 429, or with no readable response at all when the request it refused was
// the browser's CORS preflight. Pages that asked once and read that as an
// empty answer told people with a full account "You need to create a CV
// first". Two more tries, a little apart, get through nearly every time.
// Only for reads, where asking again is always safe.
const RETRY_DELAYS_MS = [400, 1000]

export async function fetchWithRetry(url: string, init: RequestInit = {}): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    const last = attempt === RETRY_DELAYS_MS.length
    try {
      const res = await fetch(url, init)
      if (last || (res.status !== 429 && res.status < 500)) return res
    } catch (err) {
      if (last) throw err
    }
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt] + Math.random() * 200))
  }
}

export async function authedFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString()
  if (!token) throw new Error('Not authenticated')

  // Reads are retried; anything that changes data is sent once.
  const send = (options.method || 'GET').toUpperCase() === 'GET' ? fetchWithRetry : fetch
  return send(`${apiBase()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })
}

// The AI endpoints that anyone can use without an account exist twice:
// /preview/<name> for everyone, and /ai/<name> behind sign-in. The server
// cannot tell who is calling on the /preview/ one, so signed-in callers use
// /ai/, where the call counts against (and is limited by) their usage
// allowance like every other AI feature. A 429 from it means the allowance
// is used up; its body's `error` is the message to show.
const AI_PREVIEW_PATH = {
  write: '/preview/ai-write',
  'ats-score': '/preview/ats-score',
  'parse-cv': '/preview/parse-cv',
  'parse-linkedin': '/preview/parse-linkedin',
} as const

export async function postAi(name: keyof typeof AI_PREVIEW_PATH, body: unknown): Promise<Response> {
  let token: string | undefined
  try {
    token = (await fetchAuthSession()).tokens?.idToken?.toString()
  } catch {
    // not signed in
  }
  const post = (path: string, auth?: string) =>
    fetch(`${apiBase()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(auth ? { Authorization: `Bearer ${auth}` } : {}) },
      body: JSON.stringify(body),
    })
  if (!token) return post(AI_PREVIEW_PATH[name])
  const res = await post(`/ai/${name}`, token)
  // A backend from before the /ai/ routes existed answers 404; fall back
  // rather than break the feature.
  return res.status === 404 ? post(AI_PREVIEW_PATH[name]) : res
}

export default api

